import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import StockReceiptSideMsg from '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptSideMsg';
import { buildStockReceiptColumns } from '#/modules/imsMgmt/receipts/stockReceipt/data/stockReceiptColumns';
import { getStockReceiptApiErrorMessage } from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import { applyReceiptDateRangeToFilters } from '#/modules/imsMgmt/receipts/stockReceipt/helper/receiptDateListFilter';
import useStockReceiptList from '#/modules/imsMgmt/receipts/stockReceipt/hooks/useStockReceiptList';
import { hydrateWarehouseFilterLabels } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';

const STOCK_RECEIPT_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  receiptNo: '進貨單號',
  receiptDate: '製造商開單日',
  status: '狀態',
  warehouse: '倉別',
};

const StockReceiptListPage = () => {
  const listRef = useRef(null);
  const [warehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.STOCK_RECEIPT_CREATE);
  const canUpdate = useCan(PERM.STOCK_RECEIPT_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useStockReceiptList();

  useEffect(() => {
    let cancelled = false;

    hydrateWarehouseFilterLabels(
      listQuery.filters?.warehouseId,
      warehouseLabelCache,
    ).then((didHydrate) => {
      if (!cancelled && didHydrate) {
        setLabelCacheVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [listQuery.filters?.warehouseId, warehouseLabelCache]);

  const setReceiptDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyReceiptDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'receiptDateFrom' && value === undefined) {
      setReceiptDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setReceiptDateRangeFilter]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildStockReceiptColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onReceiptDateRangeChange: setReceiptDateRangeFilter,
        warehouseLabelCache,
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setReceiptDateRangeFilter,
      warehouseLabelCache,
      labelCacheVersion,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.stockReceiptCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">入庫管理</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.stock_receipt.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getStockReceiptApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋單號、備註、事由、倉別或物料"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={STOCK_RECEIPT_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>

          <StockReceiptSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default StockReceiptListPage;
